// SystemPill organism — extracted from MessageBubble.
//
// Renders system-level events (session init, turn boundaries, exits,
// config changes, stops, crashes, respawns) as centered pill badges.

import type { Message } from '../../types';
import { asSystem, LOUD_SYSTEM_SUBTYPES, shortenModel } from '../../lib/messages';
import { useTranslation } from '../../i18n';
import type { TKey } from '../../i18n';

export interface SystemPillProps {
  message: Message;
}

// Sprint stdout-transport-replay (D013-EC-118) — subtypes that signal
// LOST OR INCOMPLETE agent output. These must never render as calm
// muted pills: the founder has to notice them (spec D2 "loud, not
// silent"). Same status-error token pattern as AttachmentChip /
// ErrorBoundary (design-discipline: tokens only). The set moved to
// `lib/messages.ts` (sprint turn-collapse-final-answer D4) — the turn
// collapse needs the same list to keep these pills outside the panel.
const LOUD_SUBTYPES = LOUD_SYSTEM_SUBTYPES;

export function SystemPill({ message }: SystemPillProps) {
  const { t } = useTranslation();
  const c = asSystem(message);
  const label = describeSystemMessage(c, t);
  if (!label) return null;
  const loud = typeof c.subtype === 'string' && LOUD_SUBTYPES.has(c.subtype);
  return (
    <div className="fade-in-0 slide-in-from-top-2 animate-in flex justify-center">
      <span
        className={
          loud
            ? 'text-caption px-2 py-1 rounded-full border border-status-error/40 bg-status-error/10 text-status-error'
            : 'text-caption px-2 py-1 rounded-full bg-card text-muted-foreground border border-border'
        }
        data-testid="system-pill"
        data-subtype={c.subtype}
      >
        {label}
      </span>
    </div>
  );
}

function describeSystemMessage(
  c: ReturnType<typeof asSystem>,
  t: (key: TKey, params?: Record<string, string | number>) => string,
): string | null {
  switch (c.subtype) {
    case 'init':
      return t('system.agentStarted');
    case 'turn_boundary':
      return t('system.newTurn');
    case 'result': {
      if ('duration_ms' in c && typeof c.duration_ms === 'number') {
        return t('system.turnCompleteWithDuration', {
          duration: `${(c.duration_ms / 1000).toFixed(1)}s`,
        });
      }
      return t('system.turnComplete');
    }
    case 'exit': {
      const status =
        'status' in c && typeof c.status === 'string' ? c.status : 'exited';
      return t('system.agentExited', { status });
    }
    case 'config_changed': {
      const to =
        'to' in c && typeof c.to === 'object' && c.to !== null
          ? (c.to as { model?: string; effort?: string })
          : null;
      // Sprint context-limit-survivable (D-C3) — the shared `shortenModel`
      // returns null on any non-model string, so this pill gets the same
      // leak protection as the assistant badge for free: an unrecognised
      // model id degrades to the generic label instead of rendering raw.
      const model = to?.model ? shortenModel(to.model) : null;
      if (model && to?.effort) {
        return t('system.switchedTo', { model, effort: to.effort });
      }
      return t('system.configChanged');
    }
    case 'stopped_by_user':
      return t('system.stopped');
    case 'crash':
      return t('system.agentCrashed');
    case 'respawning':
      return t('system.reopenedConversation');
    case 'respawned':
      return t('system.readyContinuing');
    // Sprint stdout-transport-replay (D013-EC-118) — visible loss
    // surfaces. Deliberately NOT in ConversationList's
    // HIDDEN_SYSTEM_SUBTYPES: the founder must see them.
    case 'transport_gap':
      return t('system.outputLost');
    case 'output_truncated':
      return t('system.outputTruncated');
    // Sprint context-limit-survivable (Seam C) — three founder-meaningful
    // subtypes that reached the `default` below and therefore rendered as
    // NOTHING. Each renders the row's OWN persisted `message`: the backend
    // authors these sentences in plain founder Spanish and is the only
    // place the REASON lives (which conversation, why it was shortened,
    // what to do next). The i18n key is the fallback for a row that
    // carries none — a justified deviation from the key-only pattern,
    // stated in D-C1′.
    //
    // 'compacted' and 'undelivered' are NEW subtypes (D-C1′): they used to
    // piggyback on 'respawning', which is founder-HIDDEN by design
    // (fence F1) and whose case below discards the row's `message`. The
    // hidden-list fence is not crossed — 'respawning' keeps its meaning
    // and its hiding; what stops is the overloading (D013-EC-190).
    case 'compacted':
      return persistedMessage(c) ?? t('system.conversationCompacted');
    case 'undelivered':
      return persistedMessage(c) ?? t('system.messageNotDelivered');
    // Sprint compaction-visibility (D1) — a REAL CLI compaction FAILED (a
    // different actor than 'compacted' above, F1: that subtype means OUR
    // oversize-recovery rebuilt a trimmed transcript). The backend authors
    // `message` in founder Spanish; `system.compactionFailed` is only the
    // fallback for a row that somehow carries none. The success case
    // ('compaction') never reaches here — it is routed to
    // `CompactionDivider` by `MessageBubble`'s pre-check, D11.
    case 'compaction_failed':
      return persistedMessage(c) ?? t('system.compactionFailed');
    // Sprint lazy-spawn-recovery (§2 D11) — two pills, backend-authored
    // Spanish, rendered verbatim (same `persistedMessage()` pattern as
    // 'compacted' / 'undelivered' / 'compaction_failed' above — the
    // founder-language invariant already lives backend-side).
    //
    // 'reassigned' EXISTS in the closed union already (agent-failover.ts)
    // but had no case here — F3/F4 at D013-EC-44/48/59 shipped it, this
    // is the first render. 'recovered_never_started' is NEW this sprint.
    // Neither is in `ConversationList.tsx`'s `HIDDEN_SYSTEM_SUBTYPES` —
    // a draft that reused 'respawning' for the recovery pill would have
    // shipped an invisible pill (F3, CONFIRMED at design challenge).
    case 'reassigned':
      return persistedMessage(c) ?? t('system.reassigned');
    case 'recovered_never_started':
      return persistedMessage(c) ?? t('system.recoveredNeverStarted');
    // 'error' is NOT new — `failFailoverResume` and the runner's
    // `agent.error` handler have written it since D-013. It was invisible
    // purely because there was no case here (D013-EC-191a); it is not in
    // HIDDEN_SYSTEM_SUBTYPES, so no filter change is needed. It IS in
    // LOUD_SYSTEM_SUBTYPES, which also lifts it out of the collapsed turn
    // panel (turn-boundaries.ts — the second consumer).
    case 'error': {
      // Sprint context-limit-survivable (PR 1, M9 copy pass) — the three
      // runner `agent.error` codes resolve their copy from `code`, NOT from
      // the row's own `message`, a deliberate deviation from the two cases
      // above. Reason: every such row written before the copy pass carries
      // backend English prose containing "Runner" / "session"
      // (`handleRunnerAgentError`, pre-2026-08-14), and THIS PR is what makes
      // those rows loud. Keying off the machine-readable `code` heals the
      // whole backlog on the next reload instead of only the rows written
      // from now on — the M9 mitigation. Every other producer of
      // `subtype:'error'` (`failFailoverResume`, code
      // `FAILOVER_RESUME_FAILED`) writes good per-call-site founder Spanish
      // and keeps rendering its own sentence, unchanged.
      const key = runnerErrorKey(c);
      if (key) return t(key);
      return persistedMessage(c) ?? t('system.error');
    }
    default:
      // Sprint messages-section-fixes (D-msg-11) — unknown / future
      // subtypes are developer-shaped jargon (e.g. 'consolidated',
      // 'reconciled-2'). Pre-fix, the raw subtype string was rendered
      // verbatim, leaking technical names to the founder. Returning
      // `null` causes the calling component to short-circuit. If a new
      // subtype deserves a founder-readable label, add an explicit
      // case here in the same PR that introduces the subtype.
      return null;
  }
}

// Sprint context-limit-survivable (PR 1, M9 copy pass) — code → i18n key for
// the three codes `handleRunnerAgentError` writes. Same shape as
// `lib/spawn-errors.ts`'s TABLE: one place to add a code, no hunt across UI
// files. Any other `code` (or none) returns null and the caller falls back to
// the row's own sentence.
const RUNNER_ERROR_KEYS: Record<string, TKey> = {
  SESSION_LOST: 'system.errorSessionLost',
  SESSION_EXPIRED: 'system.errorSessionExpired',
  RESPAWN_LOOP: 'system.errorRestartLoop',
};

function runnerErrorKey(c: ReturnType<typeof asSystem>): TKey | null {
  const code = 'code' in c && typeof c.code === 'string' ? c.code : '';
  return RUNNER_ERROR_KEYS[code] ?? null;
}

// Sprint context-limit-survivable (Seam C) — the row's own founder-facing
// sentence, or null when it is absent / blank so the caller falls back to
// its i18n key. Whitespace counts as absent: a blank pill is worse than a
// generic one.
function persistedMessage(c: ReturnType<typeof asSystem>): string | null {
  const raw = 'message' in c && typeof c.message === 'string' ? c.message.trim() : '';
  return raw.length > 0 ? raw : null;
}
